import classNames from 'classnames';
import React, { memo, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { EMPTY_OBJ } from '../../constants';
import { Scrollbars } from '../../mixins';
import {
  COLOR,
  FONT_SIZE,
  SIZE,
  fieldBorderRadius,
  fieldDisabledOpacity,
  fieldErrorShadow,
  fieldFocusShadow,
  fieldLabelFontSize,
  fieldLabelMargin,
} from '../../styles';
import { FieldInfo } from '../FieldInfo';
import { Grid } from '../Grid';
import { type TextAreaProps } from './TextArea.types';

const TextArea: React.FC<TextAreaProps> = memo(
  ({
    onChange,
    changeByProp,
    autoFocus = false,
    charactersAvailableString,
    colorMode = 'light',
    dataTestId = 'text-area',
    disabled = false,
    errorText,
    hintText,
    hintTextPosition = 'bottom',
    labelText,
    inline = false,
    onBlur,
    onFocus,
    maxLength,
    name,
    placeholder,
    resize = false,
    rows = 2,
    style = EMPTY_OBJ,
    textAreaStyle = EMPTY_OBJ,
    value,
    refFunc,
    autoExpandTextarea = false,
  }) => {
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
    const textareaWrapperRef = useRef<HTMLDivElement | null>(null);
    // State
    const [focus, setFocus] = useState(false);
    const [hover, setHover] = useState(false);

    // Functions
    const addFocus = () => setFocus(true);
    const removeFocus = () => setFocus(false);
    const addHover = () => setHover(true);
    const removeHover = () => setHover(false);

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.target.value;

      if (autoExpandTextarea && textareaWrapperRef.current !== null) {
        // replicate the value onto textarea wrapper's ::after child
        textareaWrapperRef.current.dataset.replicatedValue = value;
      }

      if (changeByProp) {
        onChange?.(changeByProp, value);
      } else {
        onChange?.({ value });
      }
    };

    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      onBlur?.(event);
    };

    const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      onFocus?.(event);
      if (autoExpandTextarea && textareaWrapperRef.current !== null) {
        textareaWrapperRef.current.dataset.replicatedValue = value;
      }
    };

    const setupReference = useCallback(
      (node: HTMLTextAreaElement) => {
        textAreaRef.current = node;
        refFunc?.(node);
      },
      [refFunc],
    );

    const shouldShowCharacterCounter = maxLength && charactersAvailableString && focus;
    const fieldsetClass = classNames('text-area', `mode-${colorMode}`, {
      error: errorText,
      disabled,
      inline,
    });

    const textAreaClass = classNames(`mode-${colorMode}`, {
      resize,
      error: errorText,
      disabled,
      autoExpandTextarea: 'auto-expand-textarea',
    });

    const labelClass = classNames(
      `mode-${colorMode}`,
      { error: errorText },
      { active: focus || hover },
      disabled,
    );

    const charactersAvailableClass = classNames('characters-available', `mode-${colorMode}`);

    return (
      <Fieldset
        className={fieldsetClass}
        onFocus={addFocus}
        onBlur={removeFocus}
        onMouseOver={addHover}
        onMouseOut={removeHover}
        style={style}
      >
        <Grid
          gridTemplateColumns={inline ? 'auto 1fr' : '100%'}
          gridTemplateRows="auto"
          gridColumnGap={inline ? '22px' : '0'}
        >
          {(labelText || hintText) && (
            <LabelWrapper className="label-wrapper" data-testid={`${dataTestId}-label`}>
              {labelText && (
                <Label className={labelClass} htmlFor={name}>
                  {labelText}
                </Label>
              )}
              {hintText && hintTextPosition === 'top' && (
                <FieldInfo colorMode={colorMode} text={hintText} />
              )}
            </LabelWrapper>
          )}
          <TextAreaWrapper
            className={'textarea-wrapper' + ` ${autoExpandTextarea ? 'auto-expand-textarea' : ''}`}
            ref={textareaWrapperRef}
          >
            <Textarea
              className={textAreaClass}
              autoFocus={autoFocus}
              disabled={disabled}
              id={name}
              maxLength={maxLength}
              name={name}
              onBlur={handleBlur}
              onChange={handleChange}
              onFocus={handleFocus}
              placeholder={placeholder}
              ref={setupReference}
              rows={rows}
              style={textAreaStyle}
              value={value}
              data-testid={dataTestId}
            />
            {shouldShowCharacterCounter && (
              <CharactersAvailable
                data-testid="characters-available"
                className={charactersAvailableClass}
              >
                {charactersAvailableString}
              </CharactersAvailable>
            )}
          </TextAreaWrapper>
          <UnderTextAreaWrapper className="under-textarea-wrapper">
            {!errorText && hintText && hintTextPosition !== 'top' && (
              <FieldInfo colorMode={colorMode} text={hintText} />
            )}
            {errorText && <FieldInfo colorMode={colorMode} type="error" text={errorText} />}
          </UnderTextAreaWrapper>
        </Grid>
      </Fieldset>
    );
  },
);

export default TextArea;
TextArea.displayName = 'TextArea';

// Styled components
const Fieldset = styled.fieldset`
  border: none;
  display: block;
  font-family: inherit;
  margin: 0 0 ${SIZE['20']};
  padding: 0;
  position: relative;
  box-sizing: border-box;
  display: block;
  width: 100%;

  ${Scrollbars({
    width: '8px',
    height: '8px',
    border: '0.5px',
    foregroundColor: 'rgba(150, 150, 150, 0.5)',
    backgroundColor: 'transparent',
  })};

  fieldset + &:last-child {
    margin-bottom: 0;
  }

  &.disabled {
    opacity: ${fieldDisabledOpacity};
    cursor: not-allowed;

    &,
    textarea:disabled,
    label {
      cursor: not-allowed;
    }

    textarea:disabled {
      background: ${COLOR['fieldDisabledBG']};
      resize: none;
    }
  }

  &.inline {
    .label-wrapper,
    label {
      width: auto;
    }

    .label-wrapper {
      align-self: flex-start;
    }

    .under-textarea-wrapper {
      grid-column-start: 2;
    }

    .field-legend {
      margin-top: 2px;
    }
  }
`;

const LabelWrapper = styled.div`
  width: 100%;
`;

const TextAreaWrapper = styled.div`
  width: 100%;
  position: relative;

  // https://css-tricks.com/the-cleanest-trick-for-autogrowing-textareas/
  &.auto-expand-textarea {
    display: grid;
  }

  &.auto-expand-textarea::after {
    content: attr(data-replicated-value) ' ';
    white-space: pre-wrap;
    visibility: hidden;
  }

  &.auto-expand-textarea > textarea {
    // prevent user resizing
    resize: none;
    overflow: hidden;
  }

  &.auto-expand-textarea > textarea,
  &.auto-expand-textarea::after {
    font: inherit;
    font-weight: 400;
    padding: ${SIZE['8']} ${SIZE['12']};

    /* place on top of each other, whichever is taller causes others to expand */
    grid-area: 1 / 1 / 2 / 2;
  }
`;

const UnderTextAreaWrapper = styled.div`
  width: 100%;
`;

const Textarea = styled.textarea`
  background: ${COLOR['white']};
  border-radius: ${fieldBorderRadius};
  border: 1px solid ${COLOR['fieldBorderColor']};
  font-family: inherit;
  font-size: ${FONT_SIZE['14']};
  line-height: 18px;
  min-height: 36px;
  padding: ${SIZE['8']} ${SIZE['12']};
  resize: none;
  box-sizing: border-box;
  display: block;
  width: 100%;

  &.disabled {
    background: ${COLOR['fieldDisabledBG']};
    resize: none;
    cursor: not-allowed;
  }

  &.resize {
    resize: vertical;
  }

  ::placeholder {
    color: ${COLOR['light-gray-darkest']};
  }

  &:not([disabled]) &:hover,
  &:focus {
    border: 1px solid ${COLOR['primary']};
    box-shadow: ${fieldFocusShadow};
    outline: none;
  }

  &.error {
    border-color: ${COLOR['red-dark']};
    box-shadow: ${fieldErrorShadow};
  }

  &.mode-dark,
  &.mode-darker {
    color: ${COLOR['white']};
    border: 1px solid ${COLOR['white']};

    &:not([disabled]) &:hover,
    &:focus {
      border: 1px solid ${COLOR['turquoise']};
    }

    ::placeholder {
      color: ${COLOR['light-gray']};
    }

    &.error {
      border-color: ${COLOR['red']};
    }
  }

  &.mode-dark {
    background: ${COLOR['dark-gray']};
  }

  &.mode-darker {
    background: ${COLOR['dark-gray-dark']};
  }

  &.auto-expand-textarea {
    display: grid;
  }
`;

const Label = styled.label`
  color: ${COLOR['fieldLabelColor']};
  cursor: pointer;
  font-size: ${fieldLabelFontSize};
  line-height: ${SIZE['16']};
  margin: ${fieldLabelMargin};
  padding: 0;
  box-sizing: border-box;
  display: block;
  width: 100%;

  &.active:not(.disabled) {
    color: ${COLOR['primary']};
  }

  &.error {
    color: ${COLOR['red-dark']};
  }

  .field-legend {
    & ~ textarea {
      margin-top: 6px;
    }
  }

  &.mode-dark,
  &.mode-darker {
    color: ${COLOR['white']};

    &.active:not(.disabled) {
      color: ${COLOR['turquoise']};
    }

    &.error {
      color: ${COLOR['red']};
    }
  }

  &.disabled {
    cursor: not-allowed;
  }
`;

const CharactersAvailable = styled.div`
  color: ${COLOR['turquoise-dark']};
  font-size: ${FONT_SIZE['12']};
  font-weight: 500;
  text-align: right;

  &.mode-dark,
  &.mode-darker {
    color: ${COLOR['turquoise']};
  }
`;
